import { Component } from "react"

/* Redux */
import { connect } from "react-redux"

/* Components */
import { InnerLayout, Pagination } from "../../components"

/* REST API */
import { list, remove } from "../../api/Studio"

/* Helpers */
import { withRouter, utils } from "../../helpers"

/* Widgets */
import { StudioForm, StudioStream } from "../../widgets/Studio"

/* Icons */
import { PlusIcon, EditIcon, TrashIcon, LockIcon, StudioIcon, BroadcastIcon, TableIcon, DealerIcon, UsersIcon, KeyIcon, LinkIcon, ServerIcon } from "../../widgets/Studio/StudioIcons"

/* Sweet alert */
import Swal from "../../helpers/alert"

/* Translate */
import { t } from "../../translates/translate"


/* SHORT SIGN OF A STUDIO — THE FIRST LETTERS OF ITS NAME */
const getInitials = (name) => {

    /* Check Name */
    if (!name || String(name).trim() === "") {
        return "—"
    }

    /* Fields */
    const parts = String(name).trim().split(/\s+/)

    /* Check Parts */
    if (parts.length === 1) {
        return parts[0].slice(0, 2).toUpperCase()
    }

    /* Send Result */
    return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase()
}


/* SHORT VIEW OF AN ADDRESS — THE PROTOCOL NEVER CARRIES INFORMATION HERE */
const getHost = (value) => {

    /* Check Value */
    if (!value) {
        return "—"
    }

    /* Send Result */
    return String(value).replace(/^[a-z]+:\/\//i, "")
}


/* Page Studio */
class Studio extends Component {

    constructor() {
        super()

        this.state = {
            status: "loading",
            data: [],
            count: 0,
            canEdit: false,
        }
    }

    componentDidMount = () => {
        window.scrollTo(0, 0)
        this.load()
    }

    componentDidUpdate = (prevProps) => {

        /* Fields */
        const { params } = this.props
        const { page } = params

        /* Check Page — a new page starts from the top with the loader */
        if (prevProps.params.page !== page) {
            window.scrollTo(0, 0)
            this.load(true)
        }

    }

    /* Get Page */
    getPage = () => {

        /* Fields */
        const { params } = this.props
        const { page } = params

        /* Check Page */
        if (Number.isInteger(parseInt(page))) {
            return parseInt(page)
        }

        return 1
    }

    /* Load Action */
    load = (reload = false) => {

        /* Fields */
        const page = this.getPage()

        /* Check Reload */
        if (reload) {
            this.setState({ status: "loading" })
        }

        /* Send to REST API */
        list(page).then((response) => {
            if (response.status === 200) {
                this.setState({
                    status: "ok",
                    data: response.data.list,
                    count: response.data.count,
                    canEdit: utils.checkBoolean(response.data.canEdit),
                })
            }
            else if (response.status === 401 || response.status === 403) {
                this.setState({ status: "permission", data: [], count: 0 })
            }
            else if (response.status === 498) {
                this.setState({ status: "key", data: [], count: 0 })
            }
            else {
                this.setState({ status: "error", data: [], count: 0 })
            }
        }).catch(() => {
            this.setState({ status: "network", data: [], count: 0 })
        })
    }

    /* Remove Action */
    remove = (item) => {

        Swal.fire({
            title: t("studio.deleteConfirm"),
            text: `${item.name} (${item.slug})`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#f44336",
            reverseButtons: true,
            confirmButtonText: t("studio.deleteButton"),
            cancelButtonText: t("common.cancel"),
        }).then((result) => {
            if (result.isConfirmed) {
                remove({ id: item.id }).then((response) => {
                    if (response.status === 200) {
                        this.load(true)
                        Swal.fire(utils.notification(t("studio.deletedSuccess"), "success"))
                    }
                    else if (response.data && response.data.message) {
                        Swal.fire(utils.notification(response.data.message, "error"))
                    }
                    else if (response.status === 498) {
                        Swal.fire(utils.notification(t("swal.invalidKey"), "error"))
                    }
                    else if (response.status === 401 || response.status === 403) {
                        Swal.fire(utils.notification(t("swal.noAccess"), "error"))
                    }
                    else {
                        Swal.fire(utils.notification(t("swal.generic"), "error"))
                    }
                }).catch(() => {
                    Swal.fire(utils.notification(t("swal.generic"), "error"))
                })
            }
        })
    }

    /* Copy Action — the token of a studio is needed outside of the panel */
    copy = (value) => {

        /* Check Value */
        if (!value) {
            return
        }

        /* Copy */
        try {
            navigator.clipboard.writeText(String(value))
            Swal.fire(utils.notification(t("studio.tokenCopied"), "success"))
        }
        catch {
            Swal.fire(utils.notification(t("swal.generic"), "error"))
        }
    }

    /* Open Stream Action */
    stream = (item) => this._stream.open({ serviceURL: item.serviceURL, recordURL: item.recordURL, name: item.name })

    /* Draw Addresses */
    _addresses = (item) => (
        <>
            <div className="std--address" title={utils.checkValue(item.serviceURL, "")}>
                <ServerIcon />
                <span>{getHost(item.serviceURL)}</span>
            </div>

            <div className="std--address" title={utils.checkValue(item.socketURL, "")}>
                <LinkIcon />
                <span>{getHost(item.socketURL)}</span>
            </div>
        </>
    )

    /* Draw Actions */
    _actions = (item) => {

        /* Fields */
        const { canEdit } = this.state

        return (
            <>
                <div onClick={() => this.stream(item)} className="std--action live" title={t("studio.streams")}>
                    <BroadcastIcon size={16} />
                </div>

                {canEdit
                    ? <>
                        <div onClick={() => this._form.open({ type: "edit", ...item })} className="std--action" title={t("studio.edit")}>
                            <EditIcon size={16} />
                        </div>

                        <div onClick={() => this.remove(item)} className="std--action red" title={t("studio.delete")}>
                            <TrashIcon size={16} />
                        </div>
                    </>
                    : <div className="std--action-locked" title={t("studio.viewOnly")}>
                        <LockIcon />
                    </div>
                }
            </>
        )
    }

    /* Draw Content */
    _content = () => {

        /* Fields */
        const { status, data, count } = this.state
        const { navigate } = this.props
        const page = this.getPage()

        /* Check Status */
        if (status !== "ok") {
            return (
                <div className="std--page">
                    <InnerLayout inner status={status} reload={() => this.load(true)} />
                </div>
            )
        }

        return (
            <div className="std--page">

                {/* Head */}
                <div className="std--table-header">
                    <div className="std--column number">#</div>
                    <div className="std--column studio">{t("studio.name")}</div>
                    <div className="std--column token">{t("studio.token")}</div>
                    <div className="std--column address">{t("studio.addresses")}</div>
                    <div className="std--column counts">{t("studio.content")}</div>
                    <div className="std--column actions" />
                </div>

                {/* Empty */}
                {data.length === 0 &&
                    <div className="std--empty">
                        <StudioIcon size={40} />
                        <span>{t("studio.empty")}</span>
                    </div>
                }

                {/* Body */}
                {data.map((item, index) =>
                    <div className="std--row" key={`${index}`}>

                        <div className="std--column number">{(page - 1) * 10 + index + 1}</div>

                        <div onClick={() => this.stream(item)} className="std--column studio">

                            <div className="std--avatar">{getInitials(item.name)}</div>

                            <div className="std--studio-text">
                                <div className="std--studio-name">{utils.checkValue(item.name, "—")}</div>
                                <div className="std--studio-slug">{utils.checkValue(item.slug, "—")}</div>
                            </div>

                        </div>

                        <div className="std--column token">
                            <div onClick={() => this.copy(item.uid)} className="std--token" title={utils.checkValue(item.uid, "")}>
                                <KeyIcon />
                                <span>{(item.uid && item.uid.length > 10) ? `${item.uid.slice(0, 5)}…${item.uid.slice(-5)}` : utils.checkValue(item.uid, "—")}</span>
                            </div>
                        </div>

                        <div className="std--column address">{this._addresses(item)}</div>

                        <div className="std--column counts">

                            <div className={`std--count ${parseInt(item.tablecount) === 0 ? "empty" : ""}`} title={t("studio.tables")}>
                                <TableIcon />
                                <span>{utils.checkValue(item.tablecount, 0)}</span>
                            </div>

                            <div className={`std--count green ${parseInt(item.dealercount) === 0 ? "empty" : ""}`} title={t("studio.dealers")}>
                                <DealerIcon />
                                <span>{utils.checkValue(item.dealercount, 0)}</span>
                            </div>

                            <div className={`std--count gray ${parseInt(item.admincount) === 0 ? "empty" : ""}`} title={t("studio.admins")}>
                                <UsersIcon size={13} />
                                <span>{utils.checkValue(item.admincount, 0)}</span>
                            </div>

                        </div>

                        <div className="std--column actions">{this._actions(item)}</div>

                    </div>
                )}

                {/* Pagination */}
                <Pagination size={10} page={page} total={count} onPageChange={(page) => navigate(`/studio/${page}`)} />

            </div>
        )
    }

    render = () => {

        /* Fields */
        const { status, canEdit } = this.state

        return (
            <div className="page">

                <div className="page-header">

                    <h1>{t("studio.title")}</h1>

                    {status === "ok" && canEdit &&
                        <div onClick={() => this._form.open({ type: "create" })} className="button std--add">
                            <PlusIcon size={14} />
                            <span>{t("common.add")}</span>
                        </div>
                    }

                </div>

                {/* Draw Content */}
                {this._content()}

                {/* Draw Form Modal */}
                <StudioForm ref={(ref) => this._form = ref} reload={() => this.load()} />

                {/* Draw Stream Modal */}
                <StudioStream ref={(ref) => this._stream = ref} />

            </div>
        )
    }

}

const mapStateToProps = (state) => ({
    language: state.language,
})

export default connect(mapStateToProps)(withRouter(Studio))
